import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronRight,
  Clock,
  FileText,
  LogOut,
  Megaphone,
  OctagonX,
  Paperclip,
  ScanLine,
  WifiOff,
  X,
} from 'lucide-react';
import type {
  FeedItem,
  FeedPost,
  KioskInfo,
  KioskRegisterResponse,
  KioskSession,
  MyDocument,
  MyReachStatus,
} from '@up2d8/contracts';
import { KIOSK_TOKEN_HEADER } from '@up2d8/contracts';
import { cn } from '@up2d8/ui';
import { API_BASE } from '../../shared/session';
import { useProductName } from '../../shared/theme';
import { KIOSK_LANGUAGES, KIOSK_TEXTS, type KioskLanguage } from './kiosk-texts';
import { SafeHtml } from '../../shared/safe-html';

/**
 * De kiosk (Stap 40, KIO-2, spec Kiosk §2–§5): een tablet in de kantine, liggend 1280 × 800.
 *
 * **Buiten de app-schil en buiten de sessie van de app.** De kiosk heeft zijn eigen toestand: het
 * toesteltoken in `localStorage` - het enige wat blijft staan - en de sessie van de medewerker
 * alleen in het geheugen van deze component. Afmelden is die toestand weggooien; er staat niets in
 * IndexedDB, en de service worker cachet op `/kiosk` niets (ADR-0076).
 *
 * Elke handeling is een tik: geen veegbeweging, geen lange druk.
 */

const TOKEN_KEY = 'up2d8.kiosk.token';
const IDLE_WARNING_MS = 50_000;
const IDLE_COUNTDOWN_S = 10;
const SESSION_WARNING_S = 30;

type Stage =
  | { name: 'loading' }
  | { name: 'setup' }
  | { name: 'ready'; info: KioskInfo }
  | { name: 'revoked' }
  | { name: 'idle' }
  | { name: 'number' }
  | { name: 'pin'; employeeNumber: string }
  | { name: 'home' }
  | { name: 'post'; postId: string }
  | { name: 'document'; document: MyDocument }
  | { name: 'confirmed'; at: Date; nextPostId: string | null }
  | { name: 'signedOut' };

interface Problem {
  code?: string;
  errors?: { path: string; message: string }[];
}

async function request<T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    device?: string | null;
    session?: string | null;
  } = {},
): Promise<{ ok: true; data: T } | { ok: false; status: number; problem: Problem }> {
  try {
    const response = await fetch(`${API_BASE}/api/v1${path}`, {
      method: options.method ?? 'GET',
      headers: {
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(options.device ? { [KIOSK_TOKEN_HEADER]: options.device } : {}),
        ...(options.session ? { authorization: `Bearer ${options.session}` } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      cache: 'no-store',
    });
    const text = await response.text();
    const json = text ? JSON.parse(text) : {};
    return response.ok
      ? { ok: true, data: json as T }
      : { ok: false, status: response.status, problem: json };
  } catch {
    return { ok: false, status: 0, problem: { code: 'OFFLINE' } };
  }
}

function clockText(date: Date, timeZone: string, language: string): string {
  return date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', timeZone });
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase();
}

export function KioskApp() {
  const [language, setLanguage] = useState<KioskLanguage>('nl');
  const tx = KIOSK_TEXTS[language];
  const [stage, setStage] = useState<Stage>({ name: 'loading' });
  const [info, setInfo] = useState<KioskInfo | null>(null);
  const [online, setOnline] = useState(true);

  // De sessie van de medewerker: alleen in het geheugen.
  const [session, setSession] = useState<KioskSession | null>(null);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [statuses, setStatuses] = useState<MyReachStatus[]>([]);
  const [documents, setDocuments] = useState<MyDocument[]>([]);
  const [loginError, setLoginError] = useState<{ text: string; locked: boolean } | null>(null);
  const device = useRef<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null,
  );

  // --- Toestel ------------------------------------------------------------------------------
  const checkDevice = useCallback(async () => {
    if (!device.current) {
      setStage({ name: 'setup' });
      return;
    }
    const result = await request<KioskInfo>('/kiosk/status', { device: device.current });
    if (result.ok) {
      setInfo(result.data);
      setOnline(true);
      setStage((current) =>
        current.name === 'loading' || current.name === 'revoked' ? { name: 'idle' } : current,
      );
    } else if (result.problem.code === 'DEVICE_REVOKED') {
      setStage({ name: 'revoked' });
    } else if (result.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      device.current = null;
      setStage({ name: 'setup' });
    } else {
      setOnline(false);
      setStage((current) => (current.name === 'loading' ? { name: 'idle' } : current));
    }
  }, []);

  useEffect(() => {
    void checkDevice();
    // Elke minuut: een ingetrokken toestel hoort dat ook als niemand iets doet.
    const timer = setInterval(() => void checkDevice(), 60_000);
    return () => clearInterval(timer);
  }, [checkDevice]);

  // --- Afmelden: alles weg -------------------------------------------------------------------
  const signOut = useCallback(
    async (show = true) => {
      const token = session?.accessToken;
      setSession(null);
      setItems([]);
      setStatuses([]);
      setDocuments([]);
      setLoginError(null);
      setLanguage('nl');
      try {
        sessionStorage.clear();
      } catch {
        /* geen sessionStorage, niets te wissen */
      }
      if (token) void request('/kiosk/logout', { method: 'POST', session: token });
      setStage(show ? { name: 'signedOut' } : { name: 'idle' });
    },
    [session],
  );

  // --- Gegevens na aanmelden ----------------------------------------------------------------
  const loadHome = useCallback(async (token: string) => {
    const [feed, docs] = await Promise.all([
      request<{ items: FeedItem[] }>('/feed?limit=30', { session: token }),
      request<{ items: MyDocument[] }>('/me/documents', { session: token }),
    ]);
    if (feed.ok) setItems(feed.data.items ?? []);
    if (docs.ok) setDocuments(docs.data.items ?? []);
    // De stand vraagt naar bepaalde berichten; zonder `postIds` is het antwoord altijd leeg, en
    // stond alles wat ooit bevestigd werd weer onder "Te bevestigen".
    const postIds = feed.ok ? (feed.data.items ?? []).map((item) => item.postId) : [];
    if (postIds.length === 0) return;
    const mine = await request<{ items: MyReachStatus[] }>(
      `/feed/my-statuses?postIds=${postIds.join(',')}`,
      { session: token },
    );
    if (mine.ok) setStatuses(mine.data.items ?? []);
  }, []);

  const acknowledged = useMemo(
    () =>
      new Set(statuses.filter((status) => status.acknowledgedAt).map((status) => status.postId)),
    [statuses],
  );
  const toConfirm = items.filter(
    (item) => item.requiresAcknowledgement && !acknowledged.has(item.postId),
  );
  const recent = items.filter((item) => !toConfirm.includes(item)).slice(0, 5);
  const docsToRead = documents.filter(
    (document) => document.requiresAcknowledgement && !document.acknowledgedAt,
  );

  const login = async (employeeNumber: string, pin: string) => {
    if (!device.current) return;
    const result = await request<KioskSession>('/kiosk/login', {
      method: 'POST',
      device: device.current,
      body: { employeeNumber, pin },
    });
    if (result.ok) {
      setLoginError(null);
      setSession(result.data);
      await loadHome(result.data.accessToken);
      setStage({ name: 'home' });
      return;
    }
    const code = result.problem.code;
    if (code === 'DEVICE_REVOKED') {
      setStage({ name: 'revoked' });
      return;
    }
    if (code === 'LOCKED' || code === 'RATE_LIMITED') {
      const seconds = Number(
        result.problem.errors?.find((error) => error.path === 'retryAfter')?.message ?? 300,
      );
      setLoginError({ text: tx.errors.locked(Math.max(1, Math.ceil(seconds / 60))), locked: true });
    } else if (code === 'NOT_ACTIVATED') {
      setLoginError({ text: tx.errors.notActivated, locked: false });
    } else {
      setLoginError({ text: tx.errors.invalid, locked: false });
    }
  };

  // --- Stilstand en sessieklok ----------------------------------------------------------------
  const lastTouch = useRef(Date.now());
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const touch = () => {
      lastTouch.current = Date.now();
    };
    window.addEventListener('pointerdown', touch);
    window.addEventListener('keydown', touch);
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => {
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
      clearInterval(timer);
    };
  }, []);

  const signedIn = session !== null;
  const idleMs = now.getTime() - lastTouch.current;
  const warning = signedIn && idleMs >= IDLE_WARNING_MS;
  const countdown = Math.max(0, IDLE_COUNTDOWN_S - Math.floor((idleMs - IDLE_WARNING_MS) / 1000));
  const sessionLeft = session
    ? Math.max(0, Math.floor((new Date(session.sessionEndsAt).getTime() - now.getTime()) / 1000))
    : 0;

  useEffect(() => {
    if (!signedIn) return;
    if ((warning && countdown <= 0) || sessionLeft <= 0) void signOut();
  }, [signedIn, warning, countdown, sessionLeft, signOut]);

  // Afgemeld en bevestigd: na een paar seconden door.
  useEffect(() => {
    if (stage.name === 'signedOut') {
      const timer = setTimeout(() => setStage({ name: 'idle' }), 3000);
      return () => clearTimeout(timer);
    }
    if (stage.name === 'confirmed') {
      const timer = setTimeout(
        () =>
          setStage(
            stage.nextPostId ? { name: 'post', postId: stage.nextPostId } : { name: 'home' },
          ),
        2000,
      );
      return () => clearTimeout(timer);
    }
    if (stage.name === 'ready') {
      const timer = setTimeout(() => setStage({ name: 'idle' }), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [stage]);

  // --- Badge: een lezer die als toetsenbord werkt ------------------------------------------
  const badge = useRef('');
  useEffect(() => {
    if (stage.name !== 'idle' && stage.name !== 'number') return;
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return;
      if (event.key === 'Enter') {
        if (badge.current.length >= 2) {
          const number = badge.current;
          badge.current = '';
          setLoginError(null);
          setStage({ name: 'pin', employeeNumber: number });
        }
        return;
      }
      if (/^[0-9A-Za-z-]$/.test(event.key)) badge.current = (badge.current + event.key).slice(-32);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage.name]);

  // --- Schermen -----------------------------------------------------------------------------
  const zoneClass = 'min-h-screen bg-background text-text-primary text-[length:var(--kiosk-text)]';

  if (stage.name === 'loading') return <div data-zone="kiosk" className={zoneClass} />;

  if (stage.name === 'setup') {
    return (
      <div data-zone="kiosk" className={zoneClass}>
        <SetupScreen
          tx={tx}
          onDone={(registered) => {
            localStorage.setItem(TOKEN_KEY, registered.deviceToken);
            device.current = registered.deviceToken;
            const { deviceToken: _token, ...rest } = registered;
            setInfo(rest);
            setStage({ name: 'ready', info: rest });
          }}
        />
      </div>
    );
  }

  if (stage.name === 'ready') {
    return (
      <div data-zone="kiosk" className={zoneClass}>
        <StatusScreen
          tone="success"
          icon={<Check className="h-14 w-14" strokeWidth={3} />}
          title={tx.ready(stage.info.name)}
          text={tx.readyHint}
          testId="kiosk-ready"
        />
      </div>
    );
  }

  if (stage.name === 'revoked') {
    return (
      <div data-zone="kiosk" className={zoneClass}>
        <StatusScreen
          tone="danger"
          icon={<OctagonX className="h-14 w-14" />}
          title={tx.revoked}
          text={tx.revokedHint}
          testId="kiosk-revoked"
        >
          <KioskButton
            variant="outline"
            onClick={() => {
              localStorage.removeItem(TOKEN_KEY);
              device.current = null;
              setStage({ name: 'setup' });
            }}
          >
            {tx.setupAgain}
          </KioskButton>
        </StatusScreen>
      </div>
    );
  }

  if (stage.name === 'signedOut') {
    return (
      <div data-zone="kiosk" className={zoneClass}>
        <StatusScreen
          tone="success"
          icon={<Check className="h-14 w-14" strokeWidth={3} />}
          title={tx.signedOut}
          text={tx.nothingLeft}
          testId="kiosk-signed-out"
          footer={tx.backIn(3)}
        />
      </div>
    );
  }

  if (stage.name === 'idle') {
    return (
      <div data-zone="kiosk" className={zoneClass}>
        <IdleScreen
          tx={tx}
          info={info}
          now={now}
          language={language}
          online={online}
          onLanguage={setLanguage}
          onSignIn={() => {
            setLoginError(null);
            setStage({ name: 'number' });
          }}
        />
      </div>
    );
  }

  if (stage.name === 'number' || stage.name === 'pin') {
    return (
      <div data-zone="kiosk" className={zoneClass}>
        <CredentialScreen
          key={stage.name}
          tx={tx}
          kind={stage.name}
          error={stage.name === 'pin' ? loginError : null}
          onCancel={() => {
            setLoginError(null);
            setStage({ name: 'idle' });
          }}
          onSubmit={(value) => {
            if (stage.name === 'number') {
              setLoginError(null);
              setStage({ name: 'pin', employeeNumber: value });
            } else {
              return login(stage.employeeNumber, value);
            }
          }}
        />
      </div>
    );
  }

  // Aangemeld: de vaste bovenbalk op elk scherm.
  const shell = (children: ReactNode) => (
    <div data-zone="kiosk" className={cn(zoneClass, 'flex flex-col')}>
      <header className="sticky top-0 z-10 flex h-[88px] items-center gap-6 border-b border-border bg-surface px-8">
        <h1 className="flex-1 font-heading text-[28px] font-semibold" data-testid="kiosk-hello">
          {tx.hello(session?.firstName ?? '')}
        </h1>
        <span
          className={cn(
            'flex items-center gap-2 rounded-pill px-5 py-2 font-heading text-[22px] font-semibold tabular-nums',
            sessionLeft <= SESSION_WARNING_S
              ? 'bg-brand-orange-tint text-[var(--orange-tint-text-aa)]'
              : 'bg-border-light text-text-secondary',
          )}
          data-testid="kiosk-session-clock"
        >
          <Clock aria-hidden className="h-6 w-6" />
          {Math.floor(sessionLeft / 60)}:{String(sessionLeft % 60).padStart(2, '0')}
        </span>
        <KioskButton variant="dark" onClick={() => void signOut()} testId="kiosk-sign-out">
          <LogOut aria-hidden className="mr-2 h-6 w-6" />
          {tx.signOut}
        </KioskButton>
      </header>
      {sessionLeft <= SESSION_WARNING_S ? (
        <div
          className="bg-brand-orange-tint px-8 py-3 font-heading text-[length:var(--kiosk-text-support)] font-semibold text-[var(--orange-tint-text-aa)]"
          role="status"
        >
          {tx.thirtySeconds}
        </div>
      ) : null}
      <main className="flex-1">{children}</main>
      {warning ? (
        <IdleWarning
          tx={tx}
          seconds={countdown}
          onStay={() => (lastTouch.current = Date.now())}
          onLeave={() => void signOut()}
        />
      ) : null}
    </div>
  );

  if (stage.name === 'home') {
    return shell(
      <div className="grid gap-8 p-8 landscape:grid-cols-2" data-testid="kiosk-home">
        <section className="flex flex-col gap-5">
          <h2 className="flex items-center gap-3 font-heading text-[length:var(--kiosk-section-title)] font-semibold">
            {tx.toConfirm}
            {toConfirm.length > 0 ? (
              <span className="rounded-pill bg-[var(--brand-orange-aa)] px-3 py-0.5 text-[20px] text-text-on-dark">
                {toConfirm.length}
              </span>
            ) : null}
          </h2>
          {toConfirm.length === 0 ? (
            <KioskCard>
              <p
                className="flex items-center gap-3 text-[length:var(--kiosk-text)] text-success-tint-text"
                data-testid="kiosk-all-done"
              >
                <Check aria-hidden className="h-7 w-7" />
                {tx.allDone}
              </p>
            </KioskCard>
          ) : (
            toConfirm.map((item) => (
              <KioskCard key={item.postId} testId="kiosk-to-confirm">
                <p className="font-heading text-[24px] font-semibold">{item.title}</p>
                <KioskButton
                  variant="orange"
                  full
                  onClick={() => setStage({ name: 'post', postId: item.postId })}
                >
                  {tx.readAndConfirm}
                </KioskButton>
              </KioskCard>
            ))
          )}
        </section>
        <section className="flex flex-col gap-5">
          {recent.length > 0 ? (
            <>
              <h2 className="font-heading text-[length:var(--kiosk-section-title)] font-semibold">
                {tx.recent}
              </h2>
              <KioskCard>
                <ul className="flex flex-col">
                  {recent.map((item) => (
                    <ListRow
                      key={item.postId}
                      icon={<Megaphone className="h-6 w-6" />}
                      title={item.title}
                      meta={item.author?.name ?? ''}
                      onClick={() => setStage({ name: 'post', postId: item.postId })}
                    />
                  ))}
                </ul>
              </KioskCard>
            </>
          ) : null}
          {docsToRead.length > 0 ? (
            <>
              <h2 className="font-heading text-[length:var(--kiosk-section-title)] font-semibold">
                {tx.documents}
              </h2>
              <KioskCard>
                <ul className="flex flex-col">
                  {docsToRead.map((document) => (
                    <ListRow
                      key={document.versionId}
                      icon={<FileText className="h-6 w-6" />}
                      title={document.title}
                      meta={document.folderName ?? document.versionLabel}
                      onClick={() => setStage({ name: 'document', document })}
                    />
                  ))}
                </ul>
              </KioskCard>
            </>
          ) : null}
        </section>
      </div>,
    );
  }

  if (stage.name === 'post') {
    const index = toConfirm.findIndex((item) => item.postId === stage.postId);
    return shell(
      <PostScreen
        tx={tx}
        token={session?.accessToken ?? ''}
        postId={stage.postId}
        acknowledged={acknowledged.has(stage.postId)}
        position={index >= 0 ? { index: index + 1, total: toConfirm.length } : null}
        onBack={() => setStage({ name: 'home' })}
        onConfirmed={async () => {
          if (session) await loadHome(session.accessToken);
          const next = toConfirm.find((item) => item.postId !== stage.postId) ?? null;
          setStage({ name: 'confirmed', at: new Date(), nextPostId: next?.postId ?? null });
        }}
      />,
    );
  }

  if (stage.name === 'document') {
    return shell(
      <DocumentScreen
        tx={tx}
        token={session?.accessToken ?? ''}
        document={stage.document}
        onBack={() => setStage({ name: 'home' })}
        onConfirmed={async () => {
          if (session) await loadHome(session.accessToken);
          setStage({ name: 'confirmed', at: new Date(), nextPostId: null });
        }}
      />,
    );
  }

  // Bevestigd (6b).
  return shell(
    <StatusScreen
      tone="success"
      icon={<Check className="h-16 w-16" strokeWidth={3} />}
      title={tx.confirmedAt(clockText(stage.at, info?.timezone ?? 'Europe/Brussels', language))}
      text={tx.confirmedHint}
      testId="kiosk-confirmed"
      large
    />,
  );
}

// --- Onderdelen ------------------------------------------------------------------------------

function KioskButton({
  variant = 'primary',
  full = false,
  large = false,
  disabled = false,
  onClick,
  children,
  testId,
}: {
  variant?: 'primary' | 'orange' | 'dark' | 'outline';
  full?: boolean;
  large?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'inline-flex items-center justify-center rounded-pill px-8 font-heading font-semibold disabled:cursor-not-allowed disabled:bg-border-light disabled:text-text-secondary',
        large
          ? 'min-h-[var(--kiosk-button-large)] text-[32px]'
          : 'min-h-[var(--kiosk-button-min)] text-[22px]',
        full && 'w-full',
        variant === 'primary' && 'bg-[var(--brand-primary-aa)] text-text-on-dark',
        variant === 'orange' && 'bg-[var(--brand-orange-aa)] text-text-on-dark',
        variant === 'dark' && 'bg-brand-dark-blue text-text-on-dark',
        variant === 'outline' && 'border-2 border-border-strong bg-surface text-text-primary',
      )}
    >
      {children}
    </button>
  );
}

function KioskCard({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-4 rounded-[var(--kiosk-card-radius)] border border-border bg-surface p-[var(--kiosk-card-padding)] shadow-sm"
    >
      {children}
    </div>
  );
}

function ListRow({
  icon,
  title,
  meta,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  meta: string;
  onClick: () => void;
}) {
  return (
    <li className="border-t border-border-light first:border-t-0">
      <button
        type="button"
        onClick={onClick}
        className="flex min-h-[72px] w-full items-center gap-4 py-2 text-left"
      >
        <span
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-brand-primary-tint text-[var(--primary-tint-text-aa)]"
        >
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-heading text-[20px] font-semibold">{title}</span>
          <span className="block truncate text-[length:var(--kiosk-text-support)] text-text-secondary">
            {meta}
          </span>
        </span>
        <ChevronRight aria-hidden className="h-7 w-7 text-text-muted" />
      </button>
    </li>
  );
}

function StatusScreen({
  tone,
  icon,
  title,
  text,
  testId,
  footer,
  large = false,
  children,
}: {
  tone: 'success' | 'danger';
  icon: ReactNode;
  title: string;
  text: string;
  testId: string;
  footer?: string;
  large?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className="flex min-h-[70vh] flex-col items-center justify-center gap-6 p-12 text-center"
      data-testid={testId}
    >
      <span
        aria-hidden
        className={cn(
          'flex items-center justify-center rounded-full',
          large ? 'h-[120px] w-[120px]' : 'h-28 w-28',
          tone === 'success'
            ? 'bg-success-tint text-success-tint-text'
            : 'bg-danger-tint text-danger-tint-text',
        )}
      >
        {icon}
      </span>
      <h1 className="font-heading text-[length:var(--kiosk-title)] font-semibold">{title}</h1>
      <p className="max-w-xl text-[length:var(--kiosk-text)] text-text-secondary">{text}</p>
      {children}
      {footer ? (
        <p className="mt-8 text-[length:var(--kiosk-text-support)] text-text-muted">{footer}</p>
      ) : null}
    </div>
  );
}

/** Frame 2: rustscherm. Bewust leeg: bedrijf, kiosknaam, klok, "Aanmelden", taal. */
function IdleScreen({
  tx,
  info,
  now,
  language,
  online,
  onLanguage,
  onSignIn,
}: {
  tx: (typeof KIOSK_TEXTS)['nl'];
  info: KioskInfo | null;
  now: Date;
  language: KioskLanguage;
  online: boolean;
  onLanguage: (language: KioskLanguage) => void;
  onSignIn: () => void;
}) {
  const timeZone = info?.timezone ?? 'Europe/Brussels';
  // Zonder bedrijf het product, niet een vaste naam (MRK-2).
  const productName = useProductName();
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-between gap-6 p-12 text-center"
      data-testid="kiosk-idle"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-4">
          {info?.tenantLogoUrl ? (
            <img src={info.tenantLogoUrl} alt="" className="h-16 w-16 rounded-md object-contain" />
          ) : (
            <span
              aria-hidden
              className="flex h-16 w-16 items-center justify-center rounded-md bg-brand-dark-blue font-heading text-[24px] font-semibold text-text-on-dark"
            >
              {initials(info?.tenantName ?? productName)}
            </span>
          )}
          <span className="font-heading text-[28px] font-semibold">
            {info?.tenantName ?? productName}
          </span>
        </div>
        {info ? (
          <span className="text-[22px] text-text-secondary">{tx.kiosk(info.name)}</span>
        ) : null}
      </div>

      <div className="flex flex-col items-center gap-2">
        <span
          className="font-heading text-[136px] font-bold leading-none tabular-nums"
          data-testid="kiosk-clock"
        >
          {clockText(now, timeZone, language)}
        </span>
        <span className="text-[length:var(--kiosk-text)] text-text-secondary">
          {now.toLocaleDateString(language, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            timeZone,
          })}
        </span>
      </div>

      <div className="flex w-full max-w-xl flex-col items-center gap-4">
        {!online ? (
          <p
            className="flex items-center gap-3 rounded-md bg-border-light p-4 text-[length:var(--kiosk-text-support)] text-text-secondary"
            role="status"
          >
            <WifiOff aria-hidden className="h-6 w-6" />
            {tx.offline}
          </p>
        ) : null}
        <KioskButton large full disabled={!online} onClick={onSignIn} testId="kiosk-sign-in">
          {tx.signIn}
        </KioskButton>
        <span className="flex items-center gap-2 text-[length:var(--kiosk-text-support)] text-text-secondary">
          <ScanLine aria-hidden className="h-6 w-6" />
          {tx.orBadge}
        </span>
      </div>

      <div className="flex gap-3" role="group" aria-label="Taal">
        {KIOSK_LANGUAGES.map((option) => (
          <button
            key={option.code}
            type="button"
            aria-pressed={option.code === language}
            onClick={() => onLanguage(option.code)}
            className={cn(
              'min-h-[var(--kiosk-language-chip)] rounded-pill border-2 px-6 font-heading text-[20px] font-semibold',
              option.code === language
                ? 'border-[var(--brand-primary-aa)] bg-brand-primary-tint text-[var(--primary-tint-text-aa)]'
                : 'border-border-strong bg-surface text-text-secondary',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Frames 3 en 4: personeelsnummer of PIN, met het cijferpad rechts. */
function CredentialScreen({
  tx,
  kind,
  error,
  onCancel,
  onSubmit,
}: {
  tx: (typeof KIOSK_TEXTS)['nl'];
  kind: 'number' | 'pin';
  error: { text: string; locked: boolean } | null;
  onCancel: () => void;
  onSubmit: (value: string) => void | Promise<void>;
}) {
  const [value, setValue] = useState('');
  // Een dubbele tik stuurde de PIN twee keer: twee sessies, of twee mislukte pogingen richting
  // de blokkering.
  const [busy, setBusy] = useState(false);
  const max = kind === 'pin' ? 6 : 12;
  // Na een fout is de PIN leeg (spec frame 9).
  useEffect(() => {
    if (error) setValue('');
  }, [error]);

  const ready = kind === 'pin' ? value.length === 6 : value.length >= 1;
  return (
    <div
      className="grid min-h-screen items-center gap-12 px-16 py-12 landscape:grid-cols-[1fr_440px]"
      data-testid={`kiosk-${kind}`}
    >
      <div className="flex flex-col gap-6">
        <h1 className="font-heading text-[length:var(--kiosk-title)] font-semibold">
          {kind === 'pin' ? tx.typePin : tx.typeNumber}
        </h1>
        <p className="text-[length:var(--kiosk-text)] text-text-secondary">
          {kind === 'pin' ? tx.typePinHint : tx.typeNumberHint}
        </p>
        {kind === 'number' ? (
          <div
            className="flex h-24 items-center rounded-[18px] border-2 border-[var(--brand-primary-aa)] bg-surface px-6 font-heading text-[48px] font-semibold tracking-[0.2em] ring-4 ring-brand-primary-tint"
            aria-live="polite"
            data-testid="kiosk-number-value"
          >
            {value}
          </div>
        ) : (
          <div className="flex gap-4" aria-label={tx.typePin} role="img">
            {Array.from({ length: 6 }, (_, index) => (
              <span
                key={index}
                className={cn(
                  'h-[30px] w-[30px] rounded-full border-2',
                  index < value.length
                    ? 'border-[var(--brand-primary-aa)] bg-[var(--brand-primary-aa)]'
                    : 'border-border-strong',
                )}
              />
            ))}
          </div>
        )}
        {error ? (
          <p
            role="alert"
            className="flex items-start gap-3 rounded-md bg-danger-tint p-4 text-[length:var(--kiosk-text-support)] text-danger-tint-text"
            data-testid="kiosk-login-error"
          >
            <AlertCircle aria-hidden className="mt-1 h-6 w-6 shrink-0" />
            {error.text}
          </p>
        ) : null}
        <div>
          <KioskButton variant="outline" onClick={onCancel}>
            {tx.cancel}
          </KioskButton>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Keypad
          disabled={error?.locked ?? false}
          onDigit={(digit) =>
            setValue((current) => (current.length < max ? current + digit : current))
          }
          onDelete={() => setValue((current) => current.slice(0, -1))}
          label={kind === 'pin' ? tx.typePin : tx.typeNumber}
        />
        <button
          type="button"
          disabled={!ready || busy || (error?.locked ?? false)}
          onClick={async () => {
            setBusy(true);
            try {
              await onSubmit(value);
            } finally {
              setBusy(false);
            }
          }}
          data-testid="kiosk-submit"
          className="min-h-20 w-full rounded-pill bg-[var(--brand-primary-aa)] font-heading text-[26px] font-semibold text-text-on-dark disabled:bg-border-light disabled:text-text-secondary"
        >
          {kind === 'pin' ? tx.signIn : tx.next}
        </button>
      </div>
    </div>
  );
}

function Keypad({
  onDigit,
  onDelete,
  disabled,
  label,
}: {
  onDigit: (digit: string) => void;
  onDelete: () => void;
  disabled: boolean;
  label: string;
}) {
  const key =
    'min-h-[var(--kiosk-keypad-min)] rounded-[18px] border border-border bg-surface font-heading text-[38px] font-semibold shadow-sm disabled:opacity-50';
  return (
    <div className="grid grid-cols-3 gap-[14px]" role="group" aria-label={label}>
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
        <button
          key={digit}
          type="button"
          className={key}
          disabled={disabled}
          onClick={() => onDigit(digit)}
        >
          {digit}
        </button>
      ))}
      <span />
      <button type="button" className={key} disabled={disabled} onClick={() => onDigit('0')}>
        0
      </button>
      <button type="button" className={key} disabled={disabled} onClick={onDelete} aria-label="⌫">
        <X aria-hidden className="mx-auto h-9 w-9" />
      </button>
    </div>
  );
}

/** Frame 7: na 50 s stilstand, met een aftellende ring. */
function IdleWarning({
  tx,
  seconds,
  onStay,
  onLeave,
}: {
  tx: (typeof KIOSK_TEXTS)['nl'];
  seconds: number;
  onStay: () => void;
  onLeave: () => void;
}) {
  const radius = 64;
  const circumference = 2 * Math.PI * radius;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,29,46,0.72)]"
      role="alertdialog"
      aria-labelledby="kiosk-still-there"
      data-testid="kiosk-idle-warning"
    >
      <div className="flex w-[640px] flex-col items-center gap-6 rounded-[24px] bg-surface p-10 text-center">
        <svg width="140" height="140" viewBox="0 0 140 140" aria-hidden>
          <circle cx="70" cy="70" r={radius} fill="none" stroke="var(--border)" strokeWidth="12" />
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke="var(--brand-orange-aa)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - seconds / IDLE_COUNTDOWN_S)}
            transform="rotate(-90 70 70)"
          />
          <text
            x="70"
            y="70"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="52"
            fontWeight="600"
            fill="currentColor"
          >
            {seconds}
          </text>
        </svg>
        <h2
          id="kiosk-still-there"
          className="font-heading text-[length:var(--kiosk-title)] font-semibold"
        >
          {tx.stillThere}
        </h2>
        <p className="text-[length:var(--kiosk-text)] text-text-secondary">
          {tx.signOutIn(seconds)}
        </p>
        <KioskButton large full onClick={onStay} testId="kiosk-stay">
          {tx.imHere}
        </KioskButton>
        <KioskButton variant="outline" full onClick={onLeave}>
          {tx.signOut}
        </KioskButton>
      </div>
    </div>
  );
}

/** Een bijlage of document in een viewer op het scherm; er is geen downloadknop. */
function Viewer({
  url,
  mimeType,
  onClose,
  closeLabel,
}: {
  url: string;
  mimeType: string;
  onClose: () => void;
  closeLabel: string;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex flex-col bg-[rgba(26,29,46,0.92)] p-6"
      data-testid="kiosk-viewer"
    >
      <div className="flex justify-end">
        <KioskButton variant="outline" onClick={onClose}>
          <X aria-hidden className="mr-2 h-6 w-6" />
          {closeLabel}
        </KioskButton>
      </div>
      <div className="mt-4 flex flex-1 items-center justify-center overflow-hidden rounded-[18px] bg-surface">
        {mimeType.startsWith('image/') ? (
          <img src={url} alt="" className="max-h-full max-w-full object-contain" />
        ) : (
          <iframe title="viewer" src={`${url}#toolbar=0`} className="h-full w-full" />
        )}
      </div>
    </div>
  );
}

async function fileUrl(token: string, fileId: string): Promise<string | null> {
  const result = await request<{ url: string }>(`/media/${fileId}/download`, { session: token });
  return result.ok ? result.data.url : null;
}

/** Frame 6: bericht lezen, met "Bevestig" in de vaste voet. */
function PostScreen({
  tx,
  token,
  postId,
  acknowledged,
  position,
  onBack,
  onConfirmed,
}: {
  tx: (typeof KIOSK_TEXTS)['nl'];
  token: string;
  postId: string;
  /**
   * Bevestigde deze medewerker dit bericht al? Dan geen knop "Bevestig": onder Recent stond hij
   * ook bij een bericht dat al bevestigd was (testrapport 28 okt.).
   */
  acknowledged: boolean;
  position: { index: number; total: number } | null;
  onBack: () => void;
  onConfirmed: () => void | Promise<void>;
}) {
  const [post, setPost] = useState<(FeedPost & { languageShown?: string }) | null>(null);
  const [viewer, setViewer] = useState<{ url: string; mimeType: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void (async () => {
      const result = await request<FeedPost>(`/feed/${postId}`, { session: token });
      if (result.ok) setPost(result.data);
      void request(`/feed/${postId}/seen`, { method: 'POST', session: token, body: {} });
    })();
  }, [postId, token]);

  if (!post) return <div className="p-8" />;

  return (
    <div className="flex min-h-full flex-col" data-testid="kiosk-post">
      <article className="mx-auto flex w-full max-w-[900px] flex-col gap-6 p-8 pb-40">
        <div>
          <KioskButton variant="outline" onClick={onBack}>
            <ArrowLeft aria-hidden className="mr-2 h-6 w-6" />
            {tx.back}
          </KioskButton>
        </div>
        <h1 className="font-heading text-[length:var(--kiosk-title)] font-semibold">
          {post.title}
        </h1>
        <p className="text-[length:var(--kiosk-text-support)] text-text-secondary">
          {post.author?.name ?? ''}
          {position ? ` · ${tx.postOf(position.index, position.total)}` : ''}
        </p>
        <SafeHtml
          className="prose max-w-none text-[length:var(--kiosk-reading)] leading-[var(--kiosk-reading-line-height)]"
          html={post.bodyHtml}
        />
        {post.attachments.length > 0 ? (
          <KioskCard>
            <ul className="flex flex-col">
              {post.attachments.map((attachment) => (
                <li
                  key={attachment.fileId}
                  className="flex min-h-[72px] items-center gap-4 border-t border-border-light first:border-t-0"
                >
                  <Paperclip aria-hidden className="h-6 w-6 text-text-muted" />
                  <span className="flex-1 truncate text-[20px]">{attachment.fileName}</span>
                  <KioskButton
                    variant="outline"
                    onClick={async () => {
                      const url = await fileUrl(token, attachment.fileId);
                      if (url) setViewer({ url, mimeType: attachment.mimeType });
                    }}
                  >
                    {tx.view}
                  </KioskButton>
                </li>
              ))}
            </ul>
          </KioskCard>
        ) : null}
      </article>
      {post.requiresAcknowledgement && !acknowledged ? (
        <footer className="fixed inset-x-0 bottom-0 flex items-center justify-end gap-6 border-t border-border bg-surface px-8 py-4">
          {failed ? (
            <p
              role="alert"
              className="text-[length:var(--kiosk-text-support)] text-danger"
              data-testid="kiosk-confirm-failed"
            >
              {tx.confirmFailed}
            </p>
          ) : null}
          <button
            type="button"
            disabled={busy}
            data-testid="kiosk-confirm"
            onClick={async () => {
              setBusy(true);
              setFailed(false);
              const result = await request(`/feed/${postId}/acknowledge`, {
                method: 'POST',
                session: token,
                body: { postVersion: post.version, languageShown: post.languageShown ?? 'nl' },
              });
              setBusy(false);
              if (result.ok) await onConfirmed();
              else setFailed(true);
            }}
            className="flex min-h-20 items-center gap-3 rounded-pill bg-[var(--brand-orange-aa)] px-10 font-heading text-[26px] font-semibold text-text-on-dark disabled:opacity-60"
          >
            <Check aria-hidden className="h-8 w-8" strokeWidth={3} />
            {tx.confirm}
          </button>
        </footer>
      ) : null}
      {viewer ? (
        <Viewer
          url={viewer.url}
          mimeType={viewer.mimeType}
          closeLabel={tx.close}
          onClose={() => setViewer(null)}
        />
      ) : null}
    </div>
  );
}

/** Een document om te lezen: de viewer, en "Bevestig". */
function DocumentScreen({
  tx,
  token,
  document,
  onBack,
  onConfirmed,
}: {
  tx: (typeof KIOSK_TEXTS)['nl'];
  token: string;
  document: MyDocument;
  onBack: () => void;
  onConfirmed: () => void | Promise<void>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    void fileUrl(token, document.fileId).then(setUrl);
  }, [token, document.fileId]);

  return (
    <div className="flex min-h-full flex-col" data-testid="kiosk-document">
      <div className="flex flex-1 flex-col gap-6 p-8 pb-40">
        <div className="flex items-center gap-4">
          <KioskButton variant="outline" onClick={onBack}>
            <ArrowLeft aria-hidden className="mr-2 h-6 w-6" />
            {tx.back}
          </KioskButton>
          <h1 className="font-heading text-[length:var(--kiosk-section-title)] font-semibold">
            {document.title}
          </h1>
        </div>
        <div className="h-[60vh] overflow-hidden rounded-[18px] border border-border bg-surface">
          {url ? (
            <iframe title={document.title} src={`${url}#toolbar=0`} className="h-full w-full" />
          ) : null}
        </div>
      </div>
      <footer className="fixed inset-x-0 bottom-0 flex items-center justify-end gap-6 border-t border-border bg-surface px-8 py-4">
        {failed ? (
          <p
            role="alert"
            className="text-[length:var(--kiosk-text-support)] text-danger"
            data-testid="kiosk-confirm-failed"
          >
            {tx.confirmFailed}
          </p>
        ) : null}
        <button
          type="button"
          disabled={busy}
          data-testid="kiosk-confirm-document"
          onClick={async () => {
            setBusy(true);
            setFailed(false);
            const result = await request(
              `/me/documents/versions/${document.versionId}/acknowledge`,
              {
                method: 'POST',
                session: token,
                body: {},
              },
            );
            setBusy(false);
            if (result.ok) await onConfirmed();
            else setFailed(true);
          }}
          className="flex min-h-20 items-center gap-3 rounded-pill bg-[var(--brand-orange-aa)] px-10 font-heading text-[26px] font-semibold text-text-on-dark disabled:opacity-60"
        >
          <Check aria-hidden className="h-8 w-8" strokeWidth={3} />
          {tx.confirm}
        </button>
      </footer>
    </div>
  );
}

/** Inrichten (niet getekend): het sjabloon van frame 3 met een invoer van acht tekens. */
function SetupScreen({
  tx,
  onDone,
}: {
  tx: (typeof KIOSK_TEXTS)['nl'];
  onDone: (registered: KioskRegisterResponse) => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const compact = code
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8);
  return (
    <div
      className="flex min-h-screen flex-col justify-center gap-6 px-16 py-12"
      data-testid="kiosk-setup"
    >
      <h1 className="font-heading text-[length:var(--kiosk-title)] font-semibold">
        {tx.setupTitle}
      </h1>
      <p className="text-[length:var(--kiosk-text)] text-text-secondary">{tx.setupHint}</p>
      <label className="flex max-w-xl flex-col gap-2">
        <span className="font-heading text-[20px] font-semibold">{tx.setupCode}</span>
        <input
          value={compact.length > 4 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact}
          onChange={(event) => setCode(event.target.value)}
          autoCapitalize="characters"
          autoComplete="off"
          data-testid="kiosk-setup-code"
          className="h-24 rounded-[18px] border-2 border-[var(--brand-primary-aa)] bg-surface px-6 font-heading text-[48px] font-semibold uppercase tracking-[0.2em] ring-4 ring-brand-primary-tint focus:outline-none"
        />
      </label>
      {error ? (
        <p
          role="alert"
          className="flex max-w-xl items-start gap-3 rounded-md bg-danger-tint p-4 text-[length:var(--kiosk-text-support)] text-danger-tint-text"
        >
          <AlertCircle aria-hidden className="mt-1 h-6 w-6 shrink-0" />
          {error}
        </p>
      ) : null}
      <div>
        <KioskButton
          disabled={compact.length !== 8}
          testId="kiosk-setup-submit"
          onClick={async () => {
            const result = await request<KioskRegisterResponse>('/kiosk/register', {
              method: 'POST',
              body: { code: `${compact.slice(0, 4)}-${compact.slice(4)}` },
            });
            if (result.ok) onDone(result.data);
            else setError(tx.setupFailed);
          }}
        >
          {tx.setup}
        </KioskButton>
      </div>
    </div>
  );
}
